import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, Upload, FileText, Building2, Settings, Plus, Pencil, Trash2, Save, Lock, LogOut, Car, 
  CheckCircle, AlertCircle, Loader2, BarChart3, TrendingUp, Users, Euro, PieChart, Activity,
  AlertTriangle, Info, Asterisk, Moon, Sun, Truck, Percent, Download, Archive, Eye
} from "lucide-react";
import storage from "@/utils/storage";
import { MGB2BEditor } from "@/components/MGB2BEditor";
import { OMODA_COMPANY_CHANNELS } from "@/components/OmodaCompanyRule";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRANDS = ["SEAT", "CUPRA", "SKODA", "KIA", "SUZUKI", "MG", "OMODA_JAECOO", "CHERY"];
const BRAND_COLORS = { SEAT: "#F55E00", CUPRA: "#CF6D38", SKODA: "#4BA82E", KIA: "#05141F", SUZUKI: "#164194", MG: "#E31E24", OMODA_JAECOO: "#FF6B00", CHERY: "#1A1A1A" };

const LoginForm = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password: pw });
      onLogin();
      toast.success("Connecté");
    } catch { toast.error("Mot de passe incorrect"); }
    setLoading(false);
  };
  
  return (
    <div className="min-h-screen bg-[#F3F4F6] flex items-center justify-center">
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-[#0A0A0A] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-heading">Administration</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Mot de passe" data-testid="admin-password-input" />
            <Button type="submit" className="w-full bg-[#0A0A0A]" disabled={loading} data-testid="admin-login-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}Connexion
            </Button>
            <Link to="/" className="block text-center text-sm text-gray-500 hover:text-gray-700">Retour</Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const PDFImportTab = ({ brand, setBrand }) => {
  const [files, setFiles] = useState([]); // Multiple files
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [clientType, setClientType] = useState("particuliers");
  const [previewMode, setPreviewMode] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  
  // Gestionnaire de collage (Ctrl+V) - Ajoute à la liste
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const blob = items[i].getAsFile();
          if (blob) {
            const file = new File([blob], `screenshot_${Date.now()}.png`, { type: blob.type });
            setFiles(prev => [...prev, file]);
            setPreviewData(null);
            setResult(null);
            toast.success(`Screenshot ${files.length + 1} ajouté !`);
            e.preventDefault();
            return;
          }
        }
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [files.length]);
  
  const handleFile = (e) => {
    e.preventDefault();
    const newFiles = e.dataTransfer?.files || e.target?.files;
    if (!newFiles) return;
    
    const validTypes = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
    const validFiles = Array.from(newFiles).filter(f => validTypes.includes(f.type));
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      setPreviewData(null);
      setResult(null);
      toast.success(`${validFiles.length} fichier(s) ajouté(s)`);
    } else {
      toast.error("PDF ou image (PNG, JPG) requis");
    }
  };
  
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewData(null);
  };
  
  const clearFiles = () => {
    setFiles([]);
    setPreviewData(null);
    setResult(null);
  };
  
  // Étape 1: Prévisualiser les données extraites
  const previewExtraction = async () => {
    if (files.length === 0) {
      toast.error("Aucun fichier à analyser");
      return;
    }
    setImporting(true);
    setPreviewData(null);
    const fd = new FormData();
    files.forEach((file) => {
      fd.append("files", file, file.name);
    });
    fd.append("brand", brand);
    fd.append("client_type", clientType);
    fd.append("preview_only", "true");
    try {
      // Note: Ne PAS mettre Content-Type manuellement - axios gère le boundary automatiquement
      const r = await axios.post(`${API}/pdf/extract-multi`, fd);
      if (r.data.success) {
        setPreviewData(r.data);
        toast.success(`${r.data.data?.models?.length || 0} modèles détectés - Vérifiez avant de confirmer`);
      } else {
        toast.error(r.data.message || "Erreur d'extraction");
        setResult(r.data);
      }
    } catch (e) {
      console.error("Extraction error:", e);
      toast.error(e.response?.data?.detail || e.message || "Erreur d'extraction");
    }
    setImporting(false);
  };
  
  // Étape 2: Confirmer et importer (utilise les données déjà extraites)
  const confirmImport = async () => {
    if (!previewData || !previewData.data?.models) return;
    setImporting(true);
    try {
      // Envoyer les modèles déjà extraits pour import direct
      const r = await axios.post(`${API}/models/import-batch`, {
        brand: brand,
        models: previewData.data.models
      });
      setResult(r.data);
      setPreviewData(null);
      if (r.data.success) {
        toast.success(r.data.message);
        setFiles([]);
      } else {
        toast.error(r.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'import");
      setResult({ success: false, message: "Erreur" });
    }
    setImporting(false);
  };
  
  // Import direct
  const upload = async () => {
    if (files.length === 0) {
      toast.error("Aucun fichier à importer");
      return;
    }
    setImporting(true);
    const fd = new FormData();
    files.forEach((file) => {
      fd.append("files", file, file.name);
    });
    fd.append("brand", brand);
    fd.append("client_type", clientType);
    fd.append("preview_only", "false");
    try {
      // Note: Ne PAS mettre Content-Type manuellement - axios gère le boundary automatiquement
      const r = await axios.post(`${API}/pdf/extract-multi`, fd);
      setResult(r.data);
      if (r.data.success) {
        toast.success(r.data.message);
        setFiles([]);
      } else {
        toast.error(r.data.message || "Erreur d'import");
      }
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(e.response?.data?.detail || e.message || "Erreur");
      setResult({ success: false, message: "Erreur" });
    }
    setImporting(false);
  };
  
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-heading">
          <FileText className="w-5 h-5 text-[#E63946]" />
          Import PDF - Programme Commercial
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Info box */}
        <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-l-blue-500 p-4 rounded-r-lg">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Extraction intelligente multi-pages :</strong> Collez plusieurs screenshots et l'IA combine automatiquement les données.
          </p>
        </div>
        
        {/* Brand selector */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">1. Marque</label>
          <div className="flex flex-wrap gap-2">
            {BRANDS.map(b => (
              <Button 
                key={b} 
                variant={brand === b ? "default" : "outline"} 
                onClick={() => { setBrand(b); setPreviewData(null); setResult(null); }}
                style={brand === b ? { backgroundColor: BRAND_COLORS[b] } : {}}
              >
                {b}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Instructions pour les pages à capturer */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">2. Pages à capturer</label>
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-emerald-600" />
                <span className="font-medium text-emerald-700 dark:text-emerald-300 text-sm">Particuliers</span>
              </div>
              <p className="text-xs text-emerald-600 dark:text-emerald-400">Marges, primes, VD/VDR</p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-700 dark:text-blue-300 text-sm">Sociétés</span>
              </div>
              <p className="text-xs text-blue-600 dark:text-blue-400">PSTE, PLIB, LCD, etc.</p>
            </div>
            <div className="p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-orange-600" />
                <span className="font-medium text-orange-700 dark:text-orange-300 text-sm">Boost</span>
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-400">Primes PO, livraison</p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Faites un screenshot de chaque page et collez-les (Ctrl+V)</p>
        </div>
        
        {/* Drop zone */}
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
            3. Vos screenshots ({files.length} ajouté{files.length > 1 ? 's' : ''})
          </label>
          <div 
            className={`p-6 text-center cursor-pointer border-2 border-dashed rounded-lg transition-colors ${
              files.length > 0 ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-gray-300 dark:border-gray-600 hover:border-gray-400"
            }`} 
            onDrop={handleFile} 
            onDragOver={(e) => e.preventDefault()} 
            onClick={() => document.getElementById("pdf-input").click()}
          >
            <input id="pdf-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleFile} className="hidden" multiple />
            <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
            <p className="text-lg font-medium">Glissez, cliquez ou <span className="text-blue-500">Ctrl+V</span></p>
            <p className="text-sm text-muted-foreground mt-1">Collez vos screenshots des pages Particuliers, Sociétés et Boost</p>
            <p className="text-xs text-emerald-600 mt-2 font-medium">💡 Chaque Ctrl+V ajoute une page - collez les 3 !</p>
          </div>
          
          {/* Liste des fichiers ajoutés */}
          {files.length > 0 && (
            <div className="mt-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Fichiers à traiter :
                </span>
                <Button variant="ghost" size="sm" onClick={clearFiles} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4 mr-1" /> Tout supprimer
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {files.map((file, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-100 dark:bg-gray-800 rounded-lg group">
                    <FileText className="w-4 h-4 text-gray-500 flex-shrink-0" />
                    <span className="text-sm truncate flex-1">{file.name}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                      className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="flex gap-3">
          <Button 
            onClick={previewExtraction} 
            disabled={files.length === 0 || importing} 
            variant="outline"
            className="flex-1 h-12"
          >
            {importing && !previewData ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Eye className="w-5 h-5 mr-2" />}
            Prévisualiser
          </Button>
          <Button 
            onClick={previewData ? confirmImport : upload} 
            disabled={files.length === 0 || importing} 
            className="flex-1 h-12 text-lg" 
            style={{ backgroundColor: BRAND_COLORS[brand] }}
          >
            {importing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Upload className="w-5 h-5 mr-2" />}
            {previewData ? "Confirmer l'import" : "Importer directement"}
          </Button>
        </div>
        
        {/* Preview data */}
        {previewData && previewData.data?.models && (
          <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-5 h-5 text-amber-600" />
              <h4 className="font-semibold text-amber-700 dark:text-amber-300">
                Prévisualisation - {previewData.data.models.length} modèles détectés
              </h4>
            </div>
            <p className="text-sm text-amber-600 dark:text-amber-400 mb-3">
              Vérifiez les marges ci-dessous. Si elles semblent incorrectes, essayez l'autre type de conditions.
            </p>
            
            {/* Preview table */}
            <div className="max-h-64 overflow-y-auto bg-white dark:bg-gray-900 rounded border">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 dark:bg-gray-800 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Modèle</th>
                    <th className="text-center p-2">Marge</th>
                    <th className="text-center p-2">Prime</th>
                    <th className="text-center p-2">Boost</th>
                    <th className="text-center p-2">VD</th>
                    <th className="text-center p-2">VDR</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.data.models.map((m, i) => (
                    <tr key={i} className="border-t border-gray-200 dark:border-gray-700">
                      <td className="p-2 font-medium">{m.name}</td>
                      <td className="text-center p-2">{(m.base_margin_rate * 100).toFixed(1)}%</td>
                      <td className="text-center p-2">{m.systematic_prime?.toLocaleString() || 0}€</td>
                      <td className="text-center p-2">{m.boost_po?.toLocaleString() || 0}€</td>
                      <td className="text-center p-2">{((m.vd_rate || 0) * 100).toFixed(0)}%</td>
                      <td className="text-center p-2">{((m.vdr_rate || 0) * 100).toFixed(0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {previewData.extraction_notes && (
              <p className="text-xs text-amber-500 mt-2 italic">{previewData.extraction_notes}</p>
            )}
            
            <div className="flex gap-2 mt-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setPreviewData(null)}
              >
                Annuler
              </Button>
              <Button 
                size="sm" 
                onClick={confirmImport}
                disabled={importing}
                style={{ backgroundColor: BRAND_COLORS[brand] }}
              >
                {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Confirmer et importer
              </Button>
            </div>
          </div>
        )}
        
        {/* Results */}
        {result && (
          <div className={`p-4 rounded-lg ${result.success ? "bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800"}`}>
            <div className="flex items-start gap-3">
              {result.success ? (
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5" />
              ) : (
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={`font-medium ${result.success ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300"}`}>
                  {result.message}
                </p>
                {result.success && result.data?.models && (
                  <div className="mt-3 space-y-2">
                    {/* Archive info */}
                    {result.archived_models > 0 && (
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                        <p className="text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2">
                          <Archive className="w-4 h-4" />
                          <span>{result.archived_models} anciens modèles archivés automatiquement</span>
                        </p>
                        <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                          Accessibles via le dropdown "Date des conditions" dans le calculateur
                        </p>
                      </div>
                    )}
                    
                    {/* Models summary */}
                    <p className="text-sm text-green-600 dark:text-green-400">
                      {result.inserted_models || result.data.models.length} modèles importés
                      {result.vdr_rates_found > 0 && ` • ${result.vdr_rates_found} taux VDR`}
                      {result.company_rates_found > 0 && ` • ${result.company_rates_found} taux société`}
                    </p>
                    
                    {/* Extraction notes */}
                    {result.extraction_notes && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 italic">{result.extraction_notes}</p>
                    )}
                    
                    {/* Show warnings if margin rates seem off */}
                    {result.warnings && result.warnings.length > 0 && (
                      <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-md">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Attention - Vérifiez les marges de base :
                        </p>
                        <div className="space-y-1 text-xs text-amber-600 dark:text-amber-400">
                          {result.warnings.map((w, i) => (
                            <p key={i}>{w}</p>
                          ))}
                        </div>
                        <p className="text-xs text-amber-500 dark:text-amber-500 mt-2 italic">
                          Les marges peuvent être modifiées manuellement dans l'onglet "Modèles"
                        </p>
                      </div>
                    )}
                    
                    {/* Show models list preview */}
                    <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md max-h-48 overflow-y-auto">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Modèles importés :</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        {result.data.models.map((m, i) => (
                          <div key={i} className="flex justify-between text-gray-600 dark:text-gray-400">
                            <span className="truncate">{m.name}</span>
                            <span className="font-mono ml-2">{(m.base_margin_rate * 100).toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ModelsTab = ({ brand, setBrand }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ 
    brand: "SEAT", 
    name: "", 
    base_margin_rate: 0.09, 
    systematic_prime: 0, 
    boost_po: 0,
    boost_livraison: 0,
    trade_in_bonus: 0,
    prime_fidelite: 0,
    vd_rate: 0,
    transport_ht: 690 
  });
  
  // Archivage des conditions
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveLabel, setArchiveLabel] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [archives, setArchives] = useState([]);
  
  // Calculer le mois précédent pour le libellé par défaut
  const getPreviousMonthLabel = () => {
    const months = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
                    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
    const now = new Date();
    const prevMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    return `${months[prevMonth]} ${year}`;
  };

  useEffect(() => { load(); loadArchives(); }, [brand]);
  
  // Initialiser le libellé avec le mois précédent quand on ouvre le dialog
  useEffect(() => {
    if (archiveOpen && !archiveLabel) {
      setArchiveLabel(getPreviousMonthLabel());
    }
  }, [archiveOpen]);
  
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/models?brand=${brand}`); setModels(r.data.models || []); }
    catch { toast.error("Erreur"); }
    setLoading(false);
  };
  
  const loadArchives = async () => {
    try { 
      const r = await axios.get(`${API}/conditions/archives?brand=${brand}`); 
      setArchives(r.data.archives || []); 
    } catch (error) { 
      console.error("Error loading archives:", error);
    }
  };
  
  const archiveConditions = async () => {
    if (!archiveLabel.trim()) {
      toast.error("Veuillez entrer un libellé (ex: Mars 2026)");
      return;
    }
    setArchiving(true);
    try {
      const response = await axios.post(`${API}/conditions/archive`, {
        brand,
        label: archiveLabel.trim()
      });
      toast.success(`✅ Conditions ${brand} archivées (${archiveLabel})`);
      setArchiveOpen(false);
      setArchiveLabel("");
      loadArchives();
    } catch (error) {
      console.error("Archive error:", error);
      toast.error(`Erreur: ${error.response?.data?.detail || error.message}`);
    }
    setArchiving(false);
  };
  
  const deleteArchive = async (archiveId, label) => {
    if (window.confirm(`Supprimer l'archive "${label}" ?`)) {
      try {
        await axios.delete(`${API}/conditions/archives/${archiveId}`);
        toast.success("Archive supprimée");
        loadArchives();
      } catch { toast.error("Erreur"); }
    }
  };
  
  const save = async () => {
    try {
      if (edit) await axios.put(`${API}/models/${edit.id}`, form);
      else await axios.post(`${API}/models`, { ...form, brand });
      toast.success("Enregistré");
      setOpen(false);
      load();
    } catch { toast.error("Erreur"); }
  };
  
  const del = async (id) => { if (window.confirm("Supprimer?")) { await axios.delete(`${API}/models/${id}`); load(); } };

  const openEdit = (m) => {
    setEdit(m);
    setForm({
      brand: m.brand || brand,
      name: m.name || "",
      base_margin_rate: m.base_margin_rate || 0,
      systematic_prime: m.systematic_prime || 0,
      boost_po: m.boost_po || 0,
      boost_livraison: m.boost_livraison || 0,
      trade_in_bonus: m.trade_in_bonus || 0,
      prime_fidelite: m.prime_fidelite || 0,
      vd_rate: m.vd_rate || 0,
      transport_ht: m.transport_ht || 0,
      paint_aid_ttc: m.paint_aid_ttc || 0,
      company_rates: m.company_rates || {},
      omoda_company_aid_additive: m.omoda_company_aid_additive || false,
      vdr_rate: m.vdr_rate || 0,
      info: m.info || null,
      mg_support: m.mg_support || null,
      skoda_conditions: m.skoda_conditions || null,
      cupra_conditions: m.cupra_conditions || null,
      suzuki_conditions: m.suzuki_conditions || null,
      kia_conditions: m.kia_conditions || null,
      seat_conditions: m.seat_conditions || null,
      kia_pbv_vd_bonus_rate: m.kia_pbv_vd_bonus_rate || 0,
      kialiberty_prime: m.kialiberty_prime || 0,
      suzuki_connected_rate: m.suzuki_connected_rate || 0,
      suzuki_connected_cap_ttc: m.suzuki_connected_cap_ttc || 0,
      suzuki_non_connected_rate: m.suzuki_non_connected_rate || 0,
      suzuki_non_connected_cap_ttc: m.suzuki_non_connected_cap_ttc || 0
    });
    setOpen(true);
  };

  const openNew = () => {
    setEdit(null);
    setForm({ 
      brand, 
      name: "", 
      base_margin_rate: 0.09, 
      systematic_prime: 0, 
      boost_po: 0,
      boost_livraison: 0,
      trade_in_bonus: 0,
      prime_fidelite: 0,
      vd_rate: 0,
      transport_ht: 690,
      paint_aid_ttc: 0,
      company_rates: {}
    });
    setOpen(true);
  };

  // Types de société par marque
  const COMPANY_TYPES = {
    SEAT: ["PLIB", "PSTE_X", "PSTEL", "PSTER", "OPTA", "OPAE", "LCD"],
    CUPRA: ["PLIB", "PSTE_X", "PSTEL", "PSTER", "OPTA", "LCD"],
    SKODA: ["PLIB", "PSTE_X", "PSTEL", "OPTA", "OPAE", "LCD", "COLL", "RENT"],
    KIA: ["PLIB", "KIA_LEASE", "LLD", "GRILLE_4", "LCD", "TAXI"],
    SUZUKI: ["PNP", "VIP", "PSTE_1_9", "PSTE_10", "LLD", "LLD_PROTO", "LCD", "AUTO_ECOLE"],
    MG: ["B2B01", "B2B02", "B2B03"],
    OMODA_JAECOO: OMODA_COMPANY_CHANNELS.map((channel) => channel.value),
    CHERY: ["B2B_HORS_LLD", "LLD_MB", "AUTRES_LLD"]
  };

  const COMPANY_TYPE_LABELS = {
    PLIB: "PLIB (Prof. Libérales)",
    PSTE_X: "PSTE X (Sociétés)",
    PSTEL: "PSTE L (LLD)",
    PSTER: "PSTE R (Grands Comptes)",
    OPTA: "OPTA (Taxi/VSL)",
    OPAE: "OPAE (Auto-Écoles)",
    LCD: "LCD (Courte Durée)",
    PNP: "PNP (Prof. Nom Propre)",
    VIP: "VIP",
    PSTE_1_9: "Sociétés 1-9 véh.",
    PSTE_10: "Sociétés 10+ véh.",
    LLD: "LLD",
    LLD_PROTO: "LLD Protocolés",
    AUTO_ECOLE: "Auto-École",
    PRO: "Professionnel",
    FLEET: "Flottes",
    OJ_LEASE_AYVENS: "OJ Lease + AYVENS",
    LLD_HORS_AYVENS: "LLD (Hors Ayvens)",
    VENTES_DIRECTES_AYVENS: "Ventes Directes Ayvens",
    VENTES_DIRECTES_AUTRES: "Ventes Directes autres",
    B2B_HORS_LLD: "Ventes B2B réseau hors LLD",
    LLD_MB: "LLD Marque Blanche (OJ Lease)",
    AUTRES_LLD: "Autres LLD"
  };

  const updateCompanyRate = (type, value) => {
    if (brand === "MG" && form.mg_support) {
      setForm({ ...form, mg_support: { ...form.mg_support,
        b2b_supports_ht: { ...form.mg_support.b2b_supports_ht, [type]: Math.max(0, parseFloat(value) || 0) } } });
      return;
    }
    const newRates = { ...form.company_rates };
    if (value === "" || value === 0) {
      delete newRates[type];
    } else {
      newRates[type] = parseFloat(value) || 0;
    }
    setForm({ ...form, company_rates: newRates });
  };
  
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 font-heading"><Car className="w-5 h-5 text-[#FF3B30]" />Modèles {brand}</CardTitle>
        <div className="flex gap-2">
          <Select value={brand} onValueChange={setBrand}><SelectTrigger className="w-32"><SelectValue /></SelectTrigger><SelectContent>{BRANDS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
          
          {/* Bouton Archiver */}
          <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50">
                <Archive className="w-4 h-4 mr-2" />Archiver
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Archiver les conditions {brand}</DialogTitle>
                <DialogDescription>
                  Avant de mettre à jour les conditions commerciales, archivez les conditions actuelles pour pouvoir y revenir si nécessaire.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Label>Libellé de l'archive</Label>
                <Input 
                  placeholder="Ex: Mars 2026, T1 2026..." 
                  value={archiveLabel} 
                  onChange={(e) => setArchiveLabel(e.target.value)}
                  className="mt-2"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {models.length} modèle(s) seront archivés
                </p>
              </div>
              {/* Liste des archives existantes */}
              {archives.length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Archives existantes</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {archives.map(a => (
                      <div key={a.id} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        <span>📁 {a.label} ({a.effective_date})</span>
                        <Button variant="ghost" size="sm" className="h-6 text-red-500 hover:text-red-700" onClick={() => deleteArchive(a.id, a.label)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setArchiveOpen(false)}>Annuler</Button>
                <Button onClick={archiveConditions} disabled={archiving} className="bg-amber-500 hover:bg-amber-600">
                  {archiving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Archivage...</> : <><Archive className="w-4 h-4 mr-2" />Archiver</>}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} style={{ backgroundColor: BRAND_COLORS[brand] }}><Plus className="w-4 h-4 mr-2" />Ajouter</Button></DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{edit ? `Modifier ${form.name}` : "Nouveau modèle"}</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-4">
                <div>
                  <Label>Nom du modèle</Label>
                  <Input placeholder="Ex: KODIAQ, FABIA Gamme..." value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Marge de base (%)</Label>
                    <Input type="number" step="0.01" value={form.base_margin_rate} onChange={(e) => setForm({ ...form, base_margin_rate: parseFloat(e.target.value) || 0 })} />
                    <p className="text-xs text-gray-500 mt-1">Ex: 0.09 = 9%</p>
                  </div>
                  <div>
                    <Label>Taux VD (%)</Label>
                    {form.mg_support ? <p data-testid="admin-mg-vd-rule" className="text-sm mt-2">Support B2C HT + marge de base</p> : <>
                      <Input type="number" step="0.01" value={form.vd_rate} onChange={(e) => setForm({ ...form, vd_rate: parseFloat(e.target.value) || 0 })} />
                      <p className="text-xs text-gray-500 mt-1">Ex: 0.12 = 12%</p>
                    </>}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2"><Euro className="w-4 h-4" />Primes (en € HT)</p>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>{form.mg_support ? "Support B2C HT (maximum)" : "Prime systématique"}</Label>
                      <Input data-testid="admin-model-systematic-prime-input" type="number" min="0" value={form.systematic_prime} onChange={(e) => {
                        const value = Math.max(0, parseFloat(e.target.value) || 0);
                        setForm({ ...form, systematic_prime: value, ...(form.mg_support ? { mg_support: { ...form.mg_support, b2c_support_ht: value } } : {}) });
                      }} />
                    </div>
                    <div>
                      <Label>Boost PO</Label>
                      <Input type="number" value={form.boost_po} onChange={(e) => setForm({ ...form, boost_po: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label>Boost Livraison</Label>
                      <Input type="number" value={form.boost_livraison} onChange={(e) => setForm({ ...form, boost_livraison: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label>Aide à la reprise</Label>
                      <Input type="number" value={form.trade_in_bonus} onChange={(e) => setForm({ ...form, trade_in_bonus: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label>Prime fidélité</Label>
                      <Input type="number" value={form.prime_fidelite} onChange={(e) => setForm({ ...form, prime_fidelite: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label>Transport HT</Label>
                      <Input type="number" value={form.transport_ht} onChange={(e) => setForm({ ...form, transport_ht: parseFloat(e.target.value) || 0 })} />
                    </div>
                    {brand === "OMODA_JAECOO" && (
                      <div>
                        <Label className="text-orange-600">Aide peinture métal TTC</Label>
                        <Input type="number" value={form.paint_aid_ttc || 0} onChange={(e) => setForm({ ...form, paint_aid_ttc: parseFloat(e.target.value) || 0 })} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Section Taux Société */}
                <div className="border-t pt-4">
                  <p data-testid="admin-company-support-title" className="text-sm font-medium mb-3 flex items-center gap-2">
                    {form.mg_support ? <><Euro className="w-4 h-4" />Supports MG société (en € HT)</> : <><Percent className="w-4 h-4" />Taux Société (participation constructeur %)</>}
                  </p>
                  {Object.keys(form.mg_support?.b2b01_tiers || {}).length > 0 ? (
                    <MGB2BEditor plan={form.mg_support} onChange={(plan) => setForm({ ...form, mg_support: plan })} />
                  ) : <div className="grid grid-cols-3 gap-3">
                    {(COMPANY_TYPES[brand] || []).map(type => (
                      <div key={type} className="flex flex-col">
                        <Label className="text-xs mb-1">{COMPANY_TYPE_LABELS[type] || type}</Label>
                        <div className="flex items-center gap-1">
                          <Input 
                            type="number" 
                            step="0.01"
                            data-testid={`admin-company-support-${type.toLowerCase()}-input`}
                            placeholder="0.00"
                            value={form.mg_support ? (form.mg_support.b2b_supports_ht[type] ?? 0) : (form.company_rates?.[type] || "")}
                            onChange={(e) => updateCompanyRate(type, e.target.value)}
                            className="text-sm h-8"
                          />
                          <span className="text-xs text-gray-500">{form.mg_support ? "€ HT" : "%"}</span>
                        </div>
                      </div>
                    ))}
                  </div>}
                  <p data-testid="admin-company-support-rule" className="text-xs text-gray-500 mt-2">{form.mg_support ? "Montants forfaitaires, en complément de la marge de base. B2B hors VD ; fidélité exclue en B2B03." : "Laissez vide si pas de participation. Ex: 0.20 = 20%"}</p>
                  {form.skoda_conditions && <p data-testid="admin-skoda-conditions-rule" className="text-xs text-emerald-700 mt-2">T4 : taux sociétés totaux, base incluse. Renouvellement complémentaire : {form.skoda_conditions.renewal_aid_ht} € HT, sous conditions. Remise de référence client : {form.skoda_conditions.private_offer_ttc} € TTC.</p>}
                  {form.cupra_conditions && <p data-testid="admin-cupra-conditions-rule" className="text-xs text-muted-foreground mt-2">CUPRA T4 : taux sociétés et DEAL totaux, base incluse. VD/VDR : base + prime systématique, majoration thermique 3 % / 4 %. Reprises Raval exclusives.</p>}
                  {form.seat_conditions && <p data-testid="admin-seat-conditions-rule" className="text-xs text-muted-foreground mt-2">SEAT octobre : marge arrière PSTE R de 2 % toujours exclue. Taux sociétés/DEAL totaux, base incluse ; prime systématique cumulée en VD/VDR pour les finitions documentées.</p>}
                  {form.omoda_company_aid_additive && <p data-testid="admin-omoda-company-rule" className="text-xs text-muted-foreground mt-2">Aides sociétés en complément de la marge de base. 0,05 = 5 % d’aide, soit 10 % au total avec une base de 5 %.</p>}
                  {form.suzuki_conditions && <p data-testid="admin-suzuki-conditions-rule" className="text-xs text-muted-foreground mt-2">Suzuki Plan n°4 : participations professionnelles en complément de la marge VN. Particuliers : remise principale plafonnée à {form.suzuki_conditions.main_discount_cap_ttc} € TTC ; Booster automatique selon éligibilité, sans double comptage.</p>}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button onClick={save} style={{ backgroundColor: BRAND_COLORS[brand] }}><Save className="w-4 h-4 mr-2" />Enregistrer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Modèle</TableHead>
                  <TableHead className="text-right">Marge</TableHead>
                  <TableHead className="text-right">Prime syst.</TableHead>
                  <TableHead className="text-right">Boost PO</TableHead>
                  <TableHead className="text-right">VD</TableHead>
                  <TableHead className="text-center">{brand === "MG" ? "Supports Société HT" : "Taux Société"}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {models.map(m => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-right">{((m.base_margin_rate || 0) * 100).toFixed(1)}%</TableCell>
                    <TableCell className="text-right">{m.mg_support?.b2c_discount_linked ? "Jusqu’à " : ""}{(m.systematic_prime || 0).toLocaleString()} €</TableCell>
                    <TableCell className="text-right">{(m.boost_po || 0).toLocaleString()} €</TableCell>
                    <TableCell className="text-right">{m.mg_support ? "Support B2C" : `${((m.vd_rate || 0) * 100).toFixed(1)}%`}</TableCell>
                    <TableCell className="text-center">
                      {m.mg_support ? (
                        <span data-testid={`admin-mg-supports-${m.id}`} className="text-xs">{Object.entries(m.mg_support.b2b_supports_ht).map(([type, amount]) => `${type} : ${amount.toLocaleString("fr-FR")} €`).join(" · ")}</span>
                      ) : Object.keys(m.company_rates || {}).length > 0 ? (
                        <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700 border-orange-200">
                          {Object.keys(m.company_rates).length} types
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(m)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-red-500" onClick={() => del(m.id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== SETTINGS TAB ====================
const SettingsTab = () => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Les nouveaux mots de passe ne correspondent pas");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Mot de passe modifié avec succès");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Mot de passe actuel incorrect");
      } else {
        toast.error(error.response?.data?.detail || "Erreur lors de la modification");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Veuillez entrer le nouveau mot de passe");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-password`, {
        new_password: newPassword
      });
      toast.success("Mot de passe réinitialisé avec succès");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Change Password Card */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading">
            <Lock className="w-5 h-5 text-[#E63946]" />
            Changer le mot de passe administrateur
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="current-password">Mot de passe actuel</Label>
              <Input
                id="current-password"
                type={showPasswords ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Entrez le mot de passe actuel"
                data-testid="current-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <Input
                id="new-password"
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Entrez le nouveau mot de passe (min. 6 caractères)"
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm-password"
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmez le nouveau mot de passe"
                data-testid="confirm-password-input"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox 
                id="show-passwords" 
                checked={showPasswords}
                onCheckedChange={setShowPasswords}
              />
              <Label htmlFor="show-passwords" className="text-sm cursor-pointer">
                Afficher les mots de passe
              </Label>
            </div>
            
            <Button 
              type="submit" 
              className="bg-[#0A0A0A] hover:bg-[#1a1a1a]" 
              disabled={loading}
              data-testid="change-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Modification...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Modifier le mot de passe
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Reset Password Card */}
      <Card className="border-0 shadow-sm border-l-4 border-l-amber-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Réinitialiser le mot de passe
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Utilisez cette option uniquement si vous avez oublié le mot de passe actuel et que vous êtes déjà connecté.
            Cette action remplacera immédiatement le mot de passe sans vérification.
          </p>
          <div className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="reset-new-password">Nouveau mot de passe</Label>
              <Input
                id="reset-new-password"
                type={showPasswords ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Entrez le nouveau mot de passe"
                data-testid="reset-new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reset-confirm-password">Confirmer le mot de passe</Label>
              <Input
                id="reset-confirm-password"
                type={showPasswords ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirmez le mot de passe"
                data-testid="reset-confirm-password-input"
              />
            </div>
            
            <Button 
              type="button"
              variant="outline"
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              onClick={handleResetPassword}
              disabled={loading}
              data-testid="reset-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Réinitialisation...
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Réinitialiser le mot de passe
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ConcessionsTab = () => {
  const [concs, setConcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [edit, setEdit] = useState(null);
  const [showPassword, setShowPassword] = useState({});

  useEffect(() => { load(); }, []);
  
  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/concessions`); setConcs(r.data); }
    catch { toast.error("Erreur"); }
    setLoading(false);
  };
  
  const save = async () => {
    try {
      if (isNew) {
        await axios.post(`${API}/concessions`, edit);
        toast.success("Concession créée");
      } else {
        await axios.put(`${API}/concessions/${edit.id}`, edit);
        toast.success("Concession mise à jour");
      }
      setOpen(false);
      setEdit(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Supprimer la concession "${name}" ?`)) {
      try {
        await axios.delete(`${API}/concessions/${id}`);
        toast.success("Concession supprimée");
        load();
      } catch { toast.error("Erreur"); }
    }
  };

  const toggleBrand = (brand) => {
    if (!edit) return;
    const brands = edit.brands || [];
    if (brands.includes(brand)) {
      setEdit({ ...edit, brands: brands.filter(b => b !== brand) });
    } else {
      setEdit({ ...edit, brands: [...brands, brand] });
    }
  };

  const openNew = () => {
    setIsNew(true);
    setEdit({
      name: "",
      city: "",
      brands: [],
      password: "",
      min_margin_rate: 0.04,  // 4% par défaut
      secretary_email: "",
      sales_manager_email: "",
      director_email: ""
    });
    setOpen(true);
  };

  const openEdit = (c) => {
    setIsNew(false);
    setEdit({ ...c });
    setOpen(true);
  };
  
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 font-heading">
          <Building2 className="w-5 h-5 text-[#FF3B30]" />
          Concessions
        </CardTitle>
        <Button onClick={openNew} className="bg-[#0A0A0A]">
          <Plus className="w-4 h-4 mr-2" />Nouvelle concession
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Ville</TableHead>
                <TableHead>Marques</TableHead>
                <TableHead>Marge mini</TableHead>
                <TableHead>Accès</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {concs.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>{c.city}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {c.brands.map(b => (
                        <span key={b} className="px-2 py-0.5 text-xs rounded-full text-white" style={{ backgroundColor: BRAND_COLORS[b] || "#0A0A0A" }}>
                          {b}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-mono rounded">
                      {((c.min_margin_rate || 0.04) * 100).toFixed(1)}%
                    </span>
                  </TableCell>
                  <TableCell>
                    {c.password ? (
                      <span className="text-green-600 text-sm flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" /> Configuré
                      </span>
                    ) : (
                      <span className="text-orange-500 text-sm flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> Non configuré
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(c.id, c.name)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Edit/Create Dialog */}
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEdit(null); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{isNew ? "Nouvelle concession" : `Modifier: ${edit?.name}`}</DialogTitle>
            </DialogHeader>
            {edit && (
              <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom de la concession</Label>
                    <Input 
                      value={edit.name || ""} 
                      onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                      placeholder="Ex: Lille Diffusion Auto"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input 
                      value={edit.city || ""} 
                      onChange={(e) => setEdit({ ...edit, city: e.target.value })}
                      placeholder="Ex: Villeneuve-d'Ascq"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <Label className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4" />
                    Mot de passe d'accès vendeurs
                  </Label>
                  <Input 
                    type="text"
                    value={edit.password || ""} 
                    onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                    placeholder="Définir un mot de passe"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Ce mot de passe sera partagé par tous les vendeurs de cette concession</p>
                </div>

                {/* Marge Minimum */}
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <Label className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-blue-600" />
                    Marge minimum requise (%)
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number"
                      step="0.1"
                      min="0"
                      max="20"
                      value={((edit.min_margin_rate || 0.04) * 100).toFixed(1)} 
                      onChange={(e) => setEdit({ ...edit, min_margin_rate: parseFloat(e.target.value) / 100 || 0.04 })}
                      className="w-24 font-mono"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">%</span>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    ⚠️ Si la marge nette est inférieure à ce seuil, la commission vendeur sera réduite au forfait de 40€
                  </p>
                </div>

                {/* Brands Selection */}
                <div>
                  <Label className="mb-2 block">Marques distribuées</Label>
                  <div className="flex flex-wrap gap-2">
                    {BRANDS.map(b => {
                      const isSelected = (edit.brands || []).includes(b);
                      return (
                        <button
                          key={b}
                          type="button"
                          onClick={() => toggleBrand(b)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isSelected 
                              ? "text-white shadow-md" 
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                          style={isSelected ? { backgroundColor: BRAND_COLORS[b] } : {}}
                        >
                          {b}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#0A0A0A]" disabled={!edit?.name || !edit?.city || (edit?.brands || []).length === 0}>
                <Save className="w-4 h-4 mr-2" />
                {isNew ? "Créer" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// Commercial Programs Tab - View aids and exclusions (with editable transport, VD, VDR)
const CommercialProgramsTab = ({ brand, setBrand }) => {
  const [programs, setPrograms] = useState({});
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailModel, setDetailModel] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // Editing states
  const [editingField, setEditingField] = useState(null); // {modelId, field}
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/commercial-programs`);
      setPrograms(r.data.programs || {});
      setBrands(r.data.brands || []);
    } catch (e) {
      console.error("Error loading programs:", e);
      toast.error("Erreur lors du chargement des programmes");
    }
    setLoading(false);
  };

  const formatPercent = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const renderExclusionBadge = (model, field) => {
    const excluded = model[`${field}_excluded`] || [];
    if (excluded.length === 0) return null;
    return (
      <span className="text-orange-500 font-bold ml-1" title={`Non cumulable avec: ${excluded.join(', ')}`}>
        *
      </span>
    );
  };

  // Generic edit functions
  const startEdit = (modelId, field, currentValue) => {
    setEditingField({ modelId, field });
    // For rates, show percentage (multiply by 100)
    if (field === 'vd_rate' || field === 'vdr_rate') {
      setEditValue(((currentValue || 0) * 100).toFixed(1));
    } else {
      setEditValue(currentValue?.toString() || "0");
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  const saveEdit = async () => {
    if (!editingField) return;
    const { modelId, field } = editingField;
    
    try {
      if (field === 'transport_ht') {
        await axios.put(`${API}/models/${modelId}/transport`, {
          transport_ht: parseFloat(editValue) || 0
        });
      } else if (field === 'vd_rate' || field === 'vdr_rate') {
        // Convert percentage back to decimal
        const rateValue = (parseFloat(editValue) || 0) / 100;
        await axios.put(`${API}/models/${modelId}/rates`, {
          [field]: rateValue
        });
      }
      toast.success("Valeur mise à jour");
      cancelEdit();
      loadPrograms();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const isEditing = (modelId, field) => {
    return editingField?.modelId === modelId && editingField?.field === field;
  };

  // Render editable cell
  const renderEditableCell = (model, field, value, className = "", suffix = "") => {
    if (isEditing(model.id, field)) {
      return (
        <div className="flex items-center gap-1 justify-end">
          <Input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-20 h-8 text-right font-mono text-sm"
            step={field.includes('rate') ? "0.1" : "10"}
            min="0"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
          />
          <span className="text-xs text-muted-foreground">{suffix}</span>
          <Button size="sm" variant="ghost" onClick={saveEdit} className="h-7 w-7 p-0 text-green-600">
            <CheckCircle className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-7 w-7 p-0 text-red-600">
            <AlertCircle className="w-4 h-4" />
          </Button>
        </div>
      );
    }
    
    // Display value - for rates show percentage
    let displayValue;
    if (field === 'vd_rate' || field === 'vdr_rate') {
      displayValue = formatPercent(value);
    } else {
      displayValue = formatCurrency(value);
    }
    
    return (
      <div 
        className={`flex items-center justify-end gap-1 cursor-pointer hover:bg-opacity-70 rounded px-2 py-1 transition-colors ${className}`}
        onClick={() => startEdit(model.id, field, value)}
        title="Cliquer pour modifier"
      >
        <span className="font-mono">{displayValue}</span>
        <Pencil className="w-3 h-3 opacity-50" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-12 h-12 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Legend */}
      <Card className="border-0 shadow-sm bg-blue-50 dark:bg-blue-950 border-l-4 border-l-blue-500">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">Programmes Commerciaux</p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                Les règles d'exclusion sont <strong>extraites automatiquement</strong> des PDFs.
                Les aides avec <span className="text-orange-500 font-bold">*</span> ne sont <strong>PAS cumulables</strong> avec certains types de vente.
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                <Truck className="w-3 h-3" /> Le transport HT est modifiable et sert de base au calcul de marge.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brand Selector */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2">
            {brands.map(b => (
              <Button
                key={b}
                variant={brand === b ? "default" : "outline"}
                onClick={() => setBrand(b)}
                style={brand === b ? { backgroundColor: BRAND_COLORS[b] } : {}}
                className="font-medium"
              >
                {b}
                {programs[b] && (
                  <span className="ml-2 text-xs opacity-75">({programs[b].length})</span>
                )}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Models Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading">
            <FileText className="w-5 h-5" style={{ color: BRAND_COLORS[brand] }} />
            Programme Commercial {brand}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {programs[brand]?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[180px]">Modèle</TableHead>
                    <TableHead className="text-right">Marge Base</TableHead>
                    <TableHead className="text-right">Prime Syst.</TableHead>
                    <TableHead className="text-right">Boost PO</TableHead>
                    <TableHead className="text-right bg-blue-50 dark:bg-blue-900/30">Aide VD</TableHead>
                    <TableHead className="text-right bg-purple-50 dark:bg-purple-900/30">Aide VDR</TableHead>
                    <TableHead className="text-right bg-amber-50 dark:bg-amber-900/30">Transport HT</TableHead>
                    <TableHead>Exclusions</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {programs[brand].map((model) => (
                    <TableRow key={model.id} className={model.has_exclusions ? "bg-amber-50/30 dark:bg-amber-900/10" : ""}>
                      <TableCell className="font-medium">
                        {model.name}
                        {model.has_exclusions && (
                          <AlertTriangle className="w-4 h-4 text-amber-500 inline ml-2" />
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatPercent(model.base_margin_rate)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(model.systematic_prime)}
                        {renderExclusionBadge(model, 'systematic_prime')}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(model.boost_po)}
                        {renderExclusionBadge(model, 'boost_po')}
                      </TableCell>
                      {/* Editable VD Rate */}
                      <TableCell className="text-right bg-blue-50/50 dark:bg-blue-900/20">
                        {renderEditableCell(model, 'vd_rate', model.vd_rate, 'text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/30', '%')}
                      </TableCell>
                      {/* Editable VDR Rate */}
                      <TableCell className="text-right bg-purple-50/50 dark:bg-purple-900/20">
                        {renderEditableCell(model, 'vdr_rate', model.vdr_rate, 'text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-800/30', '%')}
                      </TableCell>
                      {/* Editable Transport */}
                      <TableCell className="text-right bg-amber-50/50 dark:bg-amber-900/20">
                        {renderEditableCell(model, 'transport_ht', model.transport_ht, 'text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-800/30', '€')}
                      </TableCell>
                      <TableCell>
                        {model.has_exclusions ? (
                          <div className="flex flex-wrap gap-1">
                            {(model.systematic_prime_excluded || []).map(e => (
                              <Badge key={`sp-${e}`} variant="outline" className="text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700">
                                Prime≠{e}
                              </Badge>
                            ))}
                            {(model.trade_in_bonus_excluded || []).map(e => (
                              <Badge key={`tb-${e}`} variant="outline" className="text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700">
                                Reprise≠{e}
                              </Badge>
                            ))}
                            {(model.boost_po_excluded || []).map(e => (
                              <Badge key={`bp-${e}`} variant="outline" className="text-xs bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700">
                                Boost≠{e}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">Aucune</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => { setDetailModel({...model}); setDetailOpen(true); }}
                          title="Voir les détails"
                        >
                          <Info className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">Aucun modèle pour cette marque</p>
          )}
        </CardContent>
      </Card>

      {/* Detail View Dialog - READ ONLY */}
      <Dialog open={detailOpen} onOpenChange={(o) => { setDetailOpen(o); if (!o) setDetailModel(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              Détails - {detailModel?.name}
            </DialogTitle>
          </DialogHeader>
          {detailModel && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <strong>Note :</strong> Les règles d'exclusion sont extraites automatiquement des PDFs commerciaux.
                Pour les modifier, importez un nouveau PDF dans l'onglet "Import PDF".
              </div>

              {/* Toutes les aides */}
              <div className="space-y-3">
                {/* Marge Base */}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">Marge Base</span>
                  <span className="font-mono">{formatPercent(detailModel.base_margin_rate)}</span>
                </div>

                {/* Prime Systématique */}
                {detailModel.systematic_prime > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Prime Systématique</span>
                      <span className="font-mono">{formatCurrency(detailModel.systematic_prime)}</span>
                    </div>
                    {(detailModel.systematic_prime_excluded || []).length > 0 && (
                      <div className="mt-2 text-sm text-orange-600">
                        ⚠️ Non cumulable avec : {detailModel.systematic_prime_excluded.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Aide Reprise */}
                {detailModel.trade_in_bonus > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Aide à la Reprise</span>
                      <span className="font-mono">{formatCurrency(detailModel.trade_in_bonus)}</span>
                    </div>
                    {(detailModel.trade_in_bonus_excluded || []).length > 0 && (
                      <div className="mt-2 text-sm text-orange-600">
                        ⚠️ Non cumulable avec : {detailModel.trade_in_bonus_excluded.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Boost PO */}
                {detailModel.boost_po > 0 && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Boost PO</span>
                      <span className="font-mono">{formatCurrency(detailModel.boost_po)}</span>
                    </div>
                    {(detailModel.boost_po_excluded || []).length > 0 && (
                      <div className="mt-2 text-sm text-orange-600">
                        ⚠️ Non cumulable avec : {detailModel.boost_po_excluded.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Aide VD */}
                {detailModel.vd_rate > 0 && (
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <span className="font-medium text-blue-800">Aide VD</span>
                    <span className="font-mono text-blue-800">{formatPercent(detailModel.vd_rate)}</span>
                  </div>
                )}

                {/* Aide VDR */}
                {detailModel.vdr_rate > 0 && (
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="font-medium text-purple-800">Aide VDR</span>
                    <span className="font-mono text-purple-800">{formatPercent(detailModel.vdr_rate)}</span>
                  </div>
                )}

                {/* Transport */}
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium">Transport HT</span>
                  <span className="font-mono">{formatCurrency(detailModel.transport_ht)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setDetailOpen(false)} className="bg-[#0A0A0A]">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dashboard Statistics Tab
const DashboardTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedConcession, setSelectedConcession] = useState("all");
  const [concessions, setConcessions] = useState([]);
  const [viewMode, setViewMode] = useState("general"); // "general" ou "comparatif"

  useEffect(() => {
    loadConcessions();
    loadStats();
  }, []);

  useEffect(() => {
    loadStats();
  }, [selectedConcession]);

  const loadConcessions = async () => {
    try {
      const r = await axios.get(`${API}/concessions`);
      setConcessions(r.data);
    } catch (e) {
      console.error("Error loading concessions:", e);
    }
  };

  const loadStats = async () => {
    setLoading(true);
    try {
      const params = selectedConcession !== "all" ? `?concession_id=${selectedConcession}` : "";
      const r = await axios.get(`${API}/dashboard/stats${params}`);
      setStats(r.data);
    } catch (e) {
      console.error("Error loading stats:", e);
      toast.error("Erreur lors du chargement des statistiques");
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);
  };

  const handleExportExcel = async () => {
    try {
      toast.info("Génération de l'export Excel...");
      const response = await axios.get(`${API}/dashboard/export-excel`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `dashboard_emcom_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export Excel téléchargé !");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export");
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info("Génération de l'export PDF...");
      const response = await axios.get(`${API}/dashboard/export-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `dashboard_emcom_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export PDF téléchargé !");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export PDF");
    }
  };

  const handleExportPPTX = async () => {
    try {
      toast.info("Génération de l'export PowerPoint...");
      const response = await axios.get(`${API}/dashboard/export-pptx`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `dashboard_emcom_${new Date().toISOString().slice(0, 10)}.pptx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export PowerPoint téléchargé !");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Erreur lors de l'export PowerPoint");
    }
  };

  // Préparer les données comparatives par concession
  const getComparativeData = () => {
    if (!stats?.concessions) return [];
    
    const data = stats.concessions.map(c => {
      const concName = c.concession_name || c._id;
      
      // Marques pour cette concession
      const brandsForConc = stats.brand_by_concession?.filter(
        b => (b._id.concession_name || b._id.concession_id) === concName
      ) || [];
      
      // Types de vente pour cette concession
      const categoriesForConc = stats.category_by_concession?.filter(
        cat => (cat._id.concession_name || cat._id.concession_id) === concName
      ) || [];
      
      // Top modèles pour cette concession
      const topModels = stats.top_models_by_concession?.[concName] || [];
      
      return {
        ...c,
        brands: brandsForConc,
        categories: categoriesForConc,
        topModels: topModels
      };
    });
    
    return data;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-12 h-12 animate-spin text-gray-400" />
      </div>
    );
  }

  const comparativeData = getComparativeData();
  const maxCount = Math.max(...(stats?.concessions?.map(c => c.count) || [1]));

  return (
    <div className="space-y-6">
      {/* Header avec toggle Vue */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <Card className="border-0 shadow-sm flex-1">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Label className="font-medium">Filtrer par concession:</Label>
              <Select value={selectedConcession} onValueChange={setSelectedConcession}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Toutes les concessions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les concessions</SelectItem>
                  {concessions.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} - {c.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        {/* Toggle Vue + Export */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={viewMode === "general" ? "default" : "outline"}
            onClick={() => setViewMode("general")}
            className="flex items-center gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Tableau Général
          </Button>
          <Button
            variant={viewMode === "comparatif" ? "default" : "outline"}
            onClick={() => setViewMode("comparatif")}
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Comparatif Sites
          </Button>
          <div className="h-8 w-px bg-gray-300 dark:bg-gray-600 mx-1 hidden sm:block" />
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-green-300 dark:bg-green-900/20 dark:hover:bg-green-900/30 dark:text-green-400 dark:border-green-700"
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4" />
            Excel
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPDF}
            className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 border-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/30 dark:text-red-400 dark:border-red-700"
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={handleExportPPTX}
            className="flex items-center gap-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/20 dark:hover:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700"
            data-testid="export-pptx-btn"
          >
            <FileText className="w-4 h-4" />
            PowerPoint
          </Button>
        </div>
      </div>

      {/* KPI Card - Total Calculs */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-500 to-blue-600 text-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-80">Total tarifications</p>
              <p className="text-4xl font-bold">{stats?.total_calculations || 0}</p>
            </div>
            <Activity className="w-12 h-12 opacity-50" />
          </div>
        </CardContent>
      </Card>

      {viewMode === "general" ? (
        /* ===== VUE TABLEAU GÉNÉRAL ===== */
        <>
          {/* Charts Row - Marques et Types de vente */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Marques les plus tarifées */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <PieChart className="w-5 h-5 text-[#FF3B30]" />
                  Marques les plus tarifées
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.brands?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.brands.map(b => {
                      const maxBrandCount = Math.max(...stats.brands.map(x => x.count));
                      const percentage = maxBrandCount > 0 ? (b.count / maxBrandCount) * 100 : 0;
                      return (
                        <div key={b._id} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="font-medium">{b._id}</span>
                            <span className="text-gray-500">{b.count} tarifications</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                            <div 
                              className="h-2.5 rounded-full transition-all duration-500" 
                              style={{ 
                                width: `${percentage}%`, 
                                backgroundColor: BRAND_COLORS[b._id] || "#0A0A0A" 
                              }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>

            {/* Répartition par type de vente */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-heading">
                  <BarChart3 className="w-5 h-5 text-[#FF3B30]" />
                  Répartition par type de vente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.categories?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.categories.map(c => {
                      const colors = {
                        PARTICULIER: "#4BA82E",
                        VD: "#3B82F6",
                        VDR: "#8B5CF6",
                        SOCIETE: "#F59E0B"
                      };
                      const labels = {
                        PARTICULIER: "Particulier",
                        VD: "Véhicule Démo",
                        VDR: "Démo Restitué",
                        SOCIETE: "Société"
                      };
                      const total = stats.categories.reduce((s, x) => s + x.count, 0);
                      const pct = total > 0 ? ((c.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={c._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-4 h-4 rounded-full" 
                              style={{ backgroundColor: colors[c._id] || "#0A0A0A" }}
                            ></div>
                            <span className="font-medium">{labels[c._id] || c._id}</span>
                          </div>
                          <div className="text-right">
                            <span className="font-bold">{c.count}</span>
                            <span className="text-gray-500 text-sm ml-2">({pct}%)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Modèles les plus tarifés */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Car className="w-5 h-5 text-[#FF3B30]" />
                Modèles les plus tarifés
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.top_models?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {stats.top_models.map((m, idx) => (
                    <div 
                      key={`${m._id.brand}-${m._id.model}`} 
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-[#0A0A0A] text-white text-xs flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <div>
                          <span 
                            className="text-xs px-2 py-0.5 rounded text-white mr-2"
                            style={{ backgroundColor: BRAND_COLORS[m._id.brand] || "#0A0A0A" }}
                          >
                            {m._id.brand}
                          </span>
                          <span className="font-medium">{m._id.model}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-bold">{m.count}</span>
                        <span className="text-gray-500 text-sm ml-1">tarif.</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Remise moyenne par marque */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Percent className="w-5 h-5 text-[#FF3B30]" />
                Remise moyenne par marque
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.brands?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Marque</TableHead>
                      <TableHead className="text-right">Tarifications</TableHead>
                      <TableHead className="text-right">Remise moyenne TTC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.brands.map(b => (
                      <TableRow key={b._id}>
                        <TableCell>
                          <span 
                            className="px-2 py-1 text-xs rounded text-white font-medium"
                            style={{ backgroundColor: BRAND_COLORS[b._id] || "#0A0A0A" }}
                          >
                            {b._id}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{b.count}</TableCell>
                        <TableCell className="text-right font-medium text-orange-600">
                          {formatCurrency(b.avg_discount || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Tarification par concession */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Building2 className="w-5 h-5 text-[#FF3B30]" />
                Tarification par concession
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.concessions?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Concession</TableHead>
                      <TableHead className="text-right">Tarifications</TableHead>
                      <TableHead className="text-right">Remise moy. TTC</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.concessions.map(c => (
                      <TableRow key={c._id}>
                        <TableCell className="font-medium">{c.concession_name || c._id}</TableCell>
                        <TableCell className="text-right">{c.count}</TableCell>
                        <TableCell className="text-right text-orange-600">{formatCurrency(c.avg_discount || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        /* ===== VUE COMPARATIF SITES ===== */
        <>
          {/* Comparatif Nombre de Tarifications */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <BarChart3 className="w-5 h-5 text-[#FF3B30]" />
                Comparatif : Nombre de tarifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparativeData.length > 0 ? (
                <div className="space-y-3">
                  {comparativeData.map((c, idx) => {
                    const percentage = maxCount > 0 ? (c.count / maxCount) * 100 : 0;
                    const colors = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];
                    return (
                      <div key={c._id} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{c.concession_name || c._id}</span>
                          <span className="font-bold">{c.count} tarifications</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                          <div 
                            className="h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2" 
                            style={{ 
                              width: `${Math.max(percentage, 5)}%`, 
                              backgroundColor: colors[idx % colors.length]
                            }}
                          >
                            <span className="text-xs text-white font-medium">{percentage.toFixed(0)}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Comparatif Remise Moyenne */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Euro className="w-5 h-5 text-[#FF3B30]" />
                Comparatif : Remise moyenne TTC
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparativeData.length > 0 ? (
                <div className="space-y-3">
                  {[...comparativeData].sort((a, b) => (b.avg_discount || 0) - (a.avg_discount || 0)).map((c, idx) => {
                    const maxDiscount = Math.max(...comparativeData.map(x => x.avg_discount || 0));
                    const percentage = maxDiscount > 0 ? ((c.avg_discount || 0) / maxDiscount) * 100 : 0;
                    return (
                      <div key={c._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">
                            {idx + 1}
                          </span>
                          <span className="font-medium">{c.concession_name || c._id}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                            <div 
                              className="h-2 rounded-full bg-orange-500 transition-all duration-500" 
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="font-bold text-orange-600 w-24 text-right">
                            {formatCurrency(c.avg_discount || 0)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Comparatif Marques par Site */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <PieChart className="w-5 h-5 text-[#FF3B30]" />
                Comparatif : Marques par site
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparativeData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {comparativeData.map(c => (
                    <div key={c._id} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <h4 className="font-medium text-sm mb-3 truncate">{c.concession_name || c._id}</h4>
                      <div className="space-y-2">
                        {c.brands.slice(0, 4).map(b => (
                          <div key={b._id.brand} className="flex items-center justify-between text-sm">
                            <span 
                              className="px-2 py-0.5 text-xs rounded text-white"
                              style={{ backgroundColor: BRAND_COLORS[b._id.brand] || "#0A0A0A" }}
                            >
                              {b._id.brand}
                            </span>
                            <span className="font-medium">{b.count}</span>
                          </div>
                        ))}
                        {c.brands.length === 0 && (
                          <p className="text-gray-400 text-xs">Aucune donnée</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Comparatif Types de vente par Site */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <TrendingUp className="w-5 h-5 text-[#FF3B30]" />
                Comparatif : Types de vente par site
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparativeData.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Concession</TableHead>
                      <TableHead className="text-center">Particulier</TableHead>
                      <TableHead className="text-center">Société</TableHead>
                      <TableHead className="text-center">VD</TableHead>
                      <TableHead className="text-center">VDR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comparativeData.map(c => {
                      const getCategoryCount = (cat) => {
                        const found = c.categories.find(x => x._id.category === cat);
                        return found ? found.count : 0;
                      };
                      return (
                        <TableRow key={c._id}>
                          <TableCell className="font-medium">{c.concession_name || c._id}</TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-sm font-medium">
                              {getCategoryCount("PARTICULIER")}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded text-sm font-medium">
                              {getCategoryCount("SOCIETE")}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-sm font-medium">
                              {getCategoryCount("VD")}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded text-sm font-medium">
                              {getCategoryCount("VDR")}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>

          {/* Top Modèles par Site */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-heading">
                <Car className="w-5 h-5 text-[#FF3B30]" />
                Comparatif : Top 3 modèles par site
              </CardTitle>
            </CardHeader>
            <CardContent>
              {comparativeData.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {comparativeData.map(c => (
                    <div key={c._id} className="p-4 border dark:border-gray-700 rounded-lg">
                      <h4 className="font-medium mb-3 text-sm">{c.concession_name || c._id}</h4>
                      <div className="space-y-2">
                        {c.topModels.map((m, idx) => (
                          <div key={`${m.brand}-${m.model}`} className="flex items-center gap-2">
                            <span className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-xs flex items-center justify-center font-bold">
                              {idx + 1}
                            </span>
                            <span 
                              className="px-1.5 py-0.5 text-[10px] rounded text-white"
                              style={{ backgroundColor: BRAND_COLORS[m.brand] || "#0A0A0A" }}
                            >
                              {m.brand}
                            </span>
                            <span className="text-sm truncate flex-1">{m.model}</span>
                            <span className="text-sm font-medium">{m.count}</span>
                          </div>
                        ))}
                        {c.topModels.length === 0 && (
                          <p className="text-gray-400 text-xs">Aucune donnée</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">Aucune donnée disponible</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default function AdminPage() {
  // Check if logged in (either as admin or as concession user)
  const [auth, setAuth] = useState(() => {
    const parsed = storage.getJSON("emcom_session");
    if (parsed) {
      // Authorized if super admin OR if logged as concession
      return parsed.is_admin === true || !!parsed.concession_id;
    }
    return false;
  });
  
  // Check if super admin (admin without concession) - has full access
  const [isSuperAdmin, setIsSuperAdmin] = useState(() => {
    const parsed = storage.getJSON("emcom_session");
    if (parsed) {
      // Super admin = is_admin true AND (no concession_id OR concession_id is "admin")
      return parsed.is_admin === true && (!parsed.concession_id || parsed.concession_id === "admin");
    }
    return false;
  });
  
  const [tab, setTab] = useState("dashboard");
  const [brand, setBrand] = useState("SEAT");
  
  // Try to get theme from context, fallback if not available
  let isDark = false;
  let toggleTheme = () => {};
  try {
    const theme = useTheme();
    isDark = theme.isDark;
    toggleTheme = theme.toggleTheme;
  } catch {
    // Theme context not available (direct admin access)
    try {
      isDark = localStorage.getItem("emcom_dark_mode") === "true";
    } catch {
      isDark = false;
    }
    toggleTheme = () => {
      try {
        const newValue = !isDark;
        localStorage.setItem("emcom_dark_mode", newValue);
        if (newValue) {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
        window.location.reload();
      } catch {}
    };
  }

  const handleLogin = () => {
    // Mark as admin in session with admin concession_id for super admin access
    const parsed = storage.getJSON("emcom_session");
    if (parsed) {
      parsed.is_admin = true;
      parsed.concession_id = "admin"; // Super admin marker
      storage.setJSON("emcom_session", parsed);
    } else {
      storage.setJSON("emcom_session", { is_admin: true, concession_id: "admin" });
    }
    setAuth(true);
    setIsSuperAdmin(true); // Enable super admin tabs
  };

  const handleLogout = () => {
    // Remove admin flag but keep session
    const parsed = storage.getJSON("emcom_session");
    if (parsed) {
      parsed.is_admin = false;
      storage.setJSON("emcom_session", parsed);
    }
    setAuth(false);
  };
  
  if (!auth) return <LoginForm onLogin={handleLogin} />;
  
  return (
    <div className="min-h-screen bg-background">
      <header className="verbaere-header text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="p-2 hover:bg-white/10 rounded-md"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E63946] flex items-center justify-center"><Settings className="w-5 h-5" /></div>
              <div>
                <h1 className="text-xl font-heading font-black">e-MCom ADMIN</h1>
                <p className="text-xs text-gray-400">by Verbaere Performance Suite</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Dark Mode Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/10" 
              onClick={toggleTheme}
              title={isDark ? "Mode clair" : "Mode sombre"}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="ghost" className="text-white hover:bg-white/10" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Déconnexion</Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="dashboard"><BarChart3 className="w-4 h-4 mr-2" />Dashboard</TabsTrigger>
            {isSuperAdmin && (
              <>
                <TabsTrigger value="programs"><FileText className="w-4 h-4 mr-2" />Programmes</TabsTrigger>
                <TabsTrigger value="pdf"><Upload className="w-4 h-4 mr-2" />Import PDF</TabsTrigger>
                <TabsTrigger value="models"><Car className="w-4 h-4 mr-2" />Modèles</TabsTrigger>
                <TabsTrigger value="concessions"><Building2 className="w-4 h-4 mr-2" />Concessions</TabsTrigger>
                <TabsTrigger value="settings"><Settings className="w-4 h-4 mr-2" />Paramètres</TabsTrigger>
              </>
            )}
          </TabsList>
          <TabsContent value="dashboard"><DashboardTab /></TabsContent>
          {isSuperAdmin && (
            <>
              <TabsContent value="programs"><CommercialProgramsTab brand={brand} setBrand={setBrand} /></TabsContent>
              <TabsContent value="pdf"><PDFImportTab brand={brand} setBrand={setBrand} /></TabsContent>
              <TabsContent value="models"><ModelsTab brand={brand} setBrand={setBrand} /></TabsContent>
              <TabsContent value="concessions"><ConcessionsTab /></TabsContent>
              <TabsContent value="settings"><SettingsTab /></TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
}
